import { useAlerts } from "components/Alerts/AlertProvider";
import { SubmissionAlert } from "components/Submission/SubmissionAlert";
import {
  GRANDFATHERABLE_REVIEWER_DUE_DATE_MESSAGES,
  REVIEWER_DUE_DATE_MESSAGES,
} from "encamp-compliance-engine/src/schemas/general/submissionReviewerMessages";
import {
  CommentThreadFragment,
  CreateSubmissionInput,
  ReviewMode,
  SignatureType,
  SubmissionDocumentInput,
  SubmissionMethod,
  SubmissionReviewerInput,
  SubmissionStatus,
  SubmissionType,
  UpdateSubmissionInput,
  Issue,
  ReviewerStatus,
} from "generated-graphql/graphql";
import { computeNeedsDocumentPreparation } from "hooks/submission/needsDocumentPreparation";
import { computeSubmissionReviewStates } from "hooks/submission/reviewStates";
import {
  useCreateSubmission,
  useGetSubmission,
  useMoveToReview,
  useUpdateSubmission,
} from "hooks/useSubmissionMutations";
import { useTenant } from "hooks/useTenant";
import { compact, pick } from "lodash-es";
import { DateTime } from "luxon";
import { CurrentUser } from "providers/user";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useSubmissionInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useSubmissionInputValidation";
import {
  getDateLikeIsoDate,
  parseDateLikeToLocalDateTime,
  toUtcMidnightIsoFromDateLike,
} from "util/dates";

import { useValidatingForm } from "./useValidatingForm";

export type DueDateWarningsByUserId = Record<string, string>;

type ReviewerSnapshotForGrandfather = {
  dueDate: string | null;
  reviewOrder: number;
};

function normalizeDueDateForComparison(value: string | DateTime | null | undefined): string | null {
  if (value == null) {
    return null;
  }

  if (DateTime.isDateTime(value)) {
    return value.isValid ? value.toISODate() : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return getDateLikeIsoDate(trimmed) ?? null;
  }

  return null;
}

function reviewerSnapshotsEqual(
  a: ReviewerSnapshotForGrandfather | undefined,
  b: ReviewerSnapshotForGrandfather | undefined
): boolean {
  if (!a || !b) {
    return false;
  }
  return a.dueDate === b.dueDate && a.reviewOrder === b.reviewOrder;
}

function getReviewerUserIdFromSchemaStack(issue: Issue): string | null {
  const segment = issue.schemaStack?.find((s) => s?.model === "SubmissionReviewer");

  const userId = segment?.name?.trim();
  return userId ? userId : null;
}

function filterReviewerPastDueDateIssues(args: {
  issues: Issue[];
  persistedReviewerSnapshotsByUserId: Map<string, ReviewerSnapshotForGrandfather>;
  currentReviewerSnapshotsByUserId: Map<string, ReviewerSnapshotForGrandfather>;
}): Issue[] {
  const { issues, persistedReviewerSnapshotsByUserId, currentReviewerSnapshotsByUserId } = args;

  const persistedReviewersSortedByOrder = Array.from(persistedReviewerSnapshotsByUserId.entries())
    .map(([userId, snap]) => ({ userId, ...snap }))
    .sort((a, b) => a.reviewOrder - b.reviewOrder);

  return issues.filter((issue) => {
    const isTargetIssue =
      issue.modelName === "SubmissionReviewer" &&
      issue.key === "dueDate" &&
      issue.message != null &&
      GRANDFATHERABLE_REVIEWER_DUE_DATE_MESSAGES.has(issue.message);

    if (!isTargetIssue) {
      return true;
    }

    const reviewerUserId = getReviewerUserIdFromSchemaStack(issue);
    if (!reviewerUserId) {
      return true;
    }

    const persistedSnap = persistedReviewerSnapshotsByUserId.get(reviewerUserId);
    if (!persistedSnap) {
      return true;
    }

    const currentSnap = currentReviewerSnapshotsByUserId.get(reviewerUserId);
    if (!reviewerSnapshotsEqual(persistedSnap, currentSnap)) {
      return true;
    }

    // Next-reviewer rule depends on the adjacent reviewer; re-validate if it changed.
    if (issue.message === REVIEWER_DUE_DATE_MESSAGES.AFTER_NEXT_REVIEWER) {
      const persistedIndex = persistedReviewersSortedByOrder.findIndex(
        (r) => r.userId === reviewerUserId
      );
      if (persistedIndex < 0) {
        return true;
      }
      const persistedNext = persistedReviewersSortedByOrder[persistedIndex + 1];
      if (persistedNext) {
        const currentNextSnap = currentReviewerSnapshotsByUserId.get(persistedNext.userId);
        const persistedNextSnap: ReviewerSnapshotForGrandfather = {
          dueDate: persistedNext.dueDate,
          reviewOrder: persistedNext.reviewOrder,
        };
        if (!reviewerSnapshotsEqual(persistedNextSnap, currentNextSnap)) {
          return true;
        }
      }
    }

    return false;
  });
}

export interface SubmissionFormData {
  title: string;
  facilityIds: string[];
  dueDate?: DateTime;
  submissionType?: SubmissionType;
  recipient: string;
  submissionMethod?: SubmissionMethod;
  notes?: string;
  reviewRequired: boolean;
  peerReviewRequired: boolean;
  signatureType: SignatureType;
  reviewMode: ReviewMode;
  reviewers: Array<{
    userId: string;
    reviewOrder: number;
    isRequired: boolean;
    isSigner: boolean;
    isStaff: boolean;
    dueDate?: DateTime;
  }>;
  comment?: string;
  tenantId: string;
}

export type SubmissionComment = CommentThreadFragment;

export interface SubmissionReviewerSigner {
  id: string;
  submissionReviewerId?: string;
  name: string;
  email: string;
  order: number;
  signerNameOverride?: string;
  signerEmailOverride?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  dueDate?: DateTime;
  isSigner?: boolean;
  isStaff: boolean;
  reviewerStatus?: ReviewerStatus | null;
}

export enum SubmitIntent {
  SAVE_DRAFT = "SAVE_DRAFT",
  PREPARE_FOR_SIGNING = "PREPARE_FOR_SIGNING",
  MOVE_TO_REVIEW = "MOVE_TO_REVIEW",
}

interface UseSubmissionFormProps {
  submissionId?: string;
  isEditMode?: boolean;
  onSubmit?: (args: { submissionId: string; openPreparationDialog?: boolean }) => void;
  submissionDocuments?: SubmissionDocumentInput[];
  attachmentDocuments?: SubmissionDocumentInput[];
  virtualTaskId?: string;
  user?: CurrentUser | null;
  initialFormData?: {
    title?: string;
    facilityIds?: string[];
    dueDate?: DateTime;
    submissionType?: SubmissionType;
    notes?: string;
    recipient?: string;
    submissionMethod?: SubmissionMethod;
  };
}

export function useSubmissionForm({
  submissionId,
  isEditMode = false,
  onSubmit,
  submissionDocuments = [],
  attachmentDocuments = [],
  virtualTaskId,
  user,
  initialFormData,
}: UseSubmissionFormProps) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();

  const [reviewers, setReviewers] = useState<SubmissionReviewerSigner[]>([]);
  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(null);
  const [staffAssignee, setStaffAssignee] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [comments, setComments] = useState<SubmissionComment[]>([]);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  const {
    data: submissionData,
    loading: loadingSubmission,
    refetch: refetchSubmission,
  } = useGetSubmission(submissionId || "", !submissionId);

  // Create a stable reference that only changes when non-comment fields change
  // This prevents comment mutations from triggering form resets via defaultValues
  // (useValidatingForm resets the form whenever defaultValues changes)
  const stableSubmissionRef = useMemo(() => {
    const submission = submissionData?.submission;
    if (!submission) return null;

    // Return a new object only when non-comment fields change
    return {
      id: submission.id,
      title: submission.title,
      dueDate: submission.dueDate,
      submissionType: submission.submissionType,
      recipient: submission.recipient,
      submissionMethod: submission.submissionMethod,
      notes: submission.notes,
      reviewRequired: submission.reviewRequired,
      peerReviewRequired: submission.peerReviewRequired,
      signatureType: submission.signatureType,
      reviewMode: submission.reviewMode,
      reviewers: submission.reviewers,
      facilitySubmissions: submission.facilitySubmissions,
      status: submission.status,
    };
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [
    submissionData?.submission?.id,
    submissionData?.submission?.title,
    submissionData?.submission?.dueDate,
    submissionData?.submission?.submissionType,
    submissionData?.submission?.recipient,
    submissionData?.submission?.submissionMethod,
    submissionData?.submission?.notes,
    submissionData?.submission?.reviewRequired,
    submissionData?.submission?.peerReviewRequired,
    submissionData?.submission?.signatureType,
    submissionData?.submission?.reviewMode,
    submissionData?.submission?.reviewers,
    submissionData?.submission?.facilitySubmissions,
    submissionData?.submission?.status,
  ]);

  const [createSubmission, { loading: creating }] = useCreateSubmission();
  const [updateSubmissionMutation, { loading: updating }] = useUpdateSubmission();
  const [moveToReviewMutation, { loading: moveToReviewLoading }] = useMoveToReview();

  const defaultValues = useMemo<SubmissionFormData>(() => {
    if (isEditMode && stableSubmissionRef) {
      const submission = stableSubmissionRef;
      return {
        title: submission.title || "",
        facilityIds: submission.facilitySubmissions?.map((f) => f.facility.id) || [],
        dueDate: submission.dueDate ? parseDateLikeToLocalDateTime(submission.dueDate) : undefined,
        submissionType: submission.submissionType
          ? (submission.submissionType as SubmissionType)
          : undefined,
        recipient: submission.recipient || "",
        submissionMethod: (submission.submissionMethod as SubmissionMethod) || undefined,
        notes: submission.notes || "",
        reviewRequired: submission.reviewRequired || false,
        peerReviewRequired: submission.peerReviewRequired || false,
        signatureType: submission.signatureType || SignatureType.None,
        reviewMode: submission.reviewMode || ReviewMode.Parallel,
        reviewers: submission.reviewers.map((r) => ({
          userId: r.user.id,
          reviewOrder: r.reviewOrder,
          isRequired: r.isRequired,
          isSigner: r.isSigner,
          isStaff: r.isStaff,
          dueDate: r.dueDate ? parseDateLikeToLocalDateTime(r.dueDate) : undefined,
        })),
        comment: "",
        // Comments are managed separately via useState and synced from submissionData
        // Don't include them here to avoid triggering form resets on comment changes
        comments: [],
        tenantId: tenantId || "",
      };
    }

    let normalizedDueDate: DateTime | undefined = undefined;
    if (initialFormData?.dueDate) {
      const d = initialFormData.dueDate;
      if ((d as any).isLuxonDateTime) {
        normalizedDueDate = d as DateTime;
      } else if (typeof d === "string") {
        normalizedDueDate = parseDateLikeToLocalDateTime(d);
      } else if (d instanceof Date) {
        normalizedDueDate = DateTime.fromJSDate(d);
      }
    }

    return {
      title: initialFormData?.title || "",
      facilityIds: initialFormData?.facilityIds || [],
      dueDate: normalizedDueDate,
      submissionType: initialFormData?.submissionType ?? undefined,
      recipient: initialFormData?.recipient || "",
      submissionMethod: initialFormData?.submissionMethod ?? undefined,
      notes: initialFormData?.notes || "",
      reviewRequired: false,
      peerReviewRequired: false,
      signatureType: SignatureType.None,
      reviewMode: ReviewMode.Parallel,
      reviewers: [],
      comment: "",
      comments: [],
      tenantId: tenantId || "",
    };
  }, [isEditMode, stableSubmissionRef, initialFormData, tenantId]);

  const persistedReviewerSnapshotsByUserId = useMemo(() => {
    const map = new Map<string, ReviewerSnapshotForGrandfather>();

    const persistedReviewers = submissionData?.submission?.reviewers ?? [];
    for (const r of persistedReviewers) {
      map.set(r.user.id, {
        dueDate: normalizeDueDateForComparison(r.dueDate),
        reviewOrder: r.reviewOrder,
      });
    }

    return map;
  }, [submissionData?.submission?.reviewers]);

  const persistedReviewerDueDateByUserId = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const [userId, snap] of persistedReviewerSnapshotsByUserId) {
      map.set(userId, snap.dueDate);
    }
    return map;
  }, [persistedReviewerSnapshotsByUserId]);

  const defaultReviewerSnapshotsByUserId = useMemo(() => {
    const map = new Map<string, ReviewerSnapshotForGrandfather>();

    const reviewers = defaultValues.reviewers ?? [];
    for (const r of reviewers) {
      map.set(r.userId, {
        dueDate: normalizeDueDateForComparison(r.dueDate),
        reviewOrder: r.reviewOrder,
      });
    }

    return map;
  }, [defaultValues.reviewers]);

  const documents = useMemo(() => {
    return [...submissionDocuments, ...attachmentDocuments];
  }, [submissionDocuments, attachmentDocuments]);

  const persistedIssues = useMemo(
    () => submissionData?.submission?.issues ?? [],
    [submissionData?.submission?.issues]
  );

  const filteredPersistedIssues = useMemo(() => {
    return filterReviewerPastDueDateIssues({
      issues: persistedIssues,
      persistedReviewerSnapshotsByUserId,
      currentReviewerSnapshotsByUserId: defaultReviewerSnapshotsByUserId,
    });
  }, [persistedIssues, persistedReviewerSnapshotsByUserId, defaultReviewerSnapshotsByUserId]);

  const validateSubmissionInput = useSubmissionInputValidation(documents);

  const validateSubmissionInputWithGrandfathering = useCallback(
    async (input: SubmissionFormData) => {
      const issues = await validateSubmissionInput(input);

      const currentReviewerSnapshotsByUserId = new Map<string, ReviewerSnapshotForGrandfather>();
      const reviewers = input.reviewers ?? [];
      for (const r of reviewers) {
        currentReviewerSnapshotsByUserId.set(r.userId, {
          dueDate: normalizeDueDateForComparison(r.dueDate),
          reviewOrder: r.reviewOrder,
        });
      }

      return filterReviewerPastDueDateIssues({
        issues,
        persistedReviewerSnapshotsByUserId,
        currentReviewerSnapshotsByUserId,
      });
    },
    [validateSubmissionInput, persistedReviewerSnapshotsByUserId]
  );

  // Skip validation for completed submissions - we don't care about compliance errors
  const isCompletedSubmission =
    submissionData?.submission?.status === SubmissionStatus.FilingComplete ||
    submissionData?.submission?.status === SubmissionStatus.NotReporting;

  // Use stable empty array reference for completed submissions
  const emptyIssues = useMemo(() => [], []);

  const form = useValidatingForm<SubmissionFormData>(
    defaultValues,
    isCompletedSubmission ? emptyIssues : filteredPersistedIssues,
    validateSubmissionInputWithGrandfathering,
    undefined, // use default dynamicFieldKeys
    { isDisplayOnly: isCompletedSubmission }
  );

  const watchedReviewers = useWatch({
    control: form.control,
    name: "reviewers",
  });

  const dueDateWarningsByUserId = useMemo<DueDateWarningsByUserId>(() => {
    const warnings: DueDateWarningsByUserId = {};

    if (!isEditMode || persistedReviewerDueDateByUserId.size === 0) {
      return warnings;
    }

    const today = DateTime.utc().startOf("day");
    const tomorrow = today.plus({ days: 1 });

    for (const reviewer of watchedReviewers ?? []) {
      const userId = reviewer.userId;
      if (!userId) continue;

      if (!persistedReviewerDueDateByUserId.has(userId)) continue;

      const currentNormalized = normalizeDueDateForComparison(reviewer.dueDate);
      const persistedNormalized = persistedReviewerDueDateByUserId.get(userId) ?? null;

      if (currentNormalized !== persistedNormalized) continue;

      if (!currentNormalized) continue;

      const parsedDate = DateTime.fromISO(currentNormalized);
      if (!parsedDate.isValid) continue;

      const dueDate = DateTime.utc(parsedDate.year, parsedDate.month, parsedDate.day);
      const isSigner = reviewer.isSigner === true;
      const minAllowed = isSigner ? tomorrow : today;

      if (dueDate < minAllowed) {
        warnings[userId] = isSigner
          ? REVIEWER_DUE_DATE_MESSAGES.SIGNER_MIN_FUTURE
          : REVIEWER_DUE_DATE_MESSAGES.PAST_DUE_DATE;
      }
    }

    return warnings;
  }, [isEditMode, persistedReviewerDueDateByUserId, watchedReviewers]);

  const {
    reset,
    watch,
    trigger,
    formState: { dirtyFields, errors },
    issues,
    isValidating,
  } = form;

  const initialDocIdsRef = useRef({
    submission: submissionDocuments,
    attachment: attachmentDocuments,
  });
  const hasUserTouchedDocsRef = useRef(false);
  const hasSetInitialDocIdsRef = useRef(false);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  // Update initialDocIdsRef when form initializes with server data in edit mode
  // This prevents validation from triggering when documents are first loaded
  // Only run once when hasInitializedForm becomes true for the first time
  useEffect(() => {
    if (isEditMode && hasInitializedForm && !hasSetInitialDocIdsRef.current) {
      initialDocIdsRef.current = {
        submission: submissionDocuments,
        attachment: attachmentDocuments,
      };
      hasUserTouchedDocsRef.current = false;
      hasSetInitialDocIdsRef.current = true;
    }
  }, [isEditMode, hasInitializedForm, submissionDocuments, attachmentDocuments]);

  /** Trigger validation when documents change, this basically makes sure that validation
   * is triggered after the documents are changed, but not on the initial render
   */
  useEffect(() => {
    const hasChanged =
      JSON.stringify(submissionDocuments) !== JSON.stringify(initialDocIdsRef.current.submission) ||
      JSON.stringify(attachmentDocuments) !== JSON.stringify(initialDocIdsRef.current.attachment);

    // Mark that user has touched documents
    if (hasChanged) {
      hasUserTouchedDocsRef.current = true;
    }

    // Validate if: user has touched docs AND (in create mode or edit is initialized)
    if (hasUserTouchedDocsRef.current && (!isEditMode || hasInitializedForm)) {
      trigger();
    }
  }, [
    documents,
    submissionDocuments,
    attachmentDocuments,
    trigger,
    isEditMode,
    hasInitializedForm,
  ]);

  const signatureType = watch("signatureType");
  const isSignatureTypeDirty = dirtyFields.signatureType;
  const isDigitalSignature = signatureType === SignatureType.Digital;

  const needsDocumentPreparation = useMemo(() => {
    return computeNeedsDocumentPreparation({
      isDigitalSignature,
      isEditMode,
      signer,
      defaultValues,
      submissionData,
      submissionDocuments,
    });
  }, [isDigitalSignature, isEditMode, submissionDocuments, defaultValues, signer, submissionData]);

  // Clear signer when signature mode changes to handle wet-ink vs digital requirements
  useEffect(() => {
    if (isSignatureTypeDirty) {
      setSigner(null);
    }
  }, [isSignatureTypeDirty]);

  useEffect(() => {
    if (isEditMode && submissionData?.submission && !hasInitializedForm) {
      const submission = submissionData.submission;

      if (submission.reviewers) {
        const mappedReviewers = submission.reviewers.map((r) => ({
          id: r.user.id,
          submissionReviewerId: r.id,
          name: r.user.person ? `${r.user.person.first} ${r.user.person.last}` : r.user.email,
          email: r.user.email,
          order: r.reviewOrder,
          isSigner: r.isSigner,
          isStaff: r.isStaff,
          reviewerStatus: r.reviewerStatus,
          signerNameOverride: r.signerNameOverride ?? undefined,
          signerEmailOverride: r.signerEmailOverride ?? undefined,
          streetAddress1: r.streetAddress1 ?? undefined,
          streetAddress2: r.streetAddress2 ?? undefined,
          city: r.city ?? undefined,
          state: r.state ?? undefined,
          zip: r.zip ?? undefined,
          country: r.country ?? undefined,
          dueDate: r.dueDate ? parseDateLikeToLocalDateTime(r.dueDate) : undefined,
        }));
        const loadedReviewers = mappedReviewers.filter((r) => !r.isSigner);
        setReviewers(loadedReviewers);

        const signer = mappedReviewers.find((r) => r.isSigner);
        if (signer) {
          setSigner(signer);
        }
      }

      if (submission.staffAssignee) {
        setStaffAssignee({
          id: submission.staffAssignee.id,
          name: submission.staffAssignee.person
            ? `${submission.staffAssignee.person.first} ${submission.staffAssignee.person.last}`
            : submission.staffAssignee.email,
          email: submission.staffAssignee.email,
        });
      }

      if (submission.comments?.length > 0) {
        setComments(submission.comments);
      }

      // Delay to ensure reset has completed and prevent race conditions
      requestAnimationFrame(() => {
        setHasInitializedForm(true);
      });
    }
  }, [isEditMode, submissionData, hasInitializedForm]);

  // Sync reviewers when submission data is refetched (e.g., after "Assign Peer Review to Me")
  useEffect(() => {
    if (hasInitializedForm && submissionData?.submission?.reviewers) {
      const mappedReviewers = submissionData.submission.reviewers.map((r) => ({
        id: r.user.id,
        submissionReviewerId: r.id,
        name: r.user.person ? `${r.user.person.first} ${r.user.person.last}` : r.user.email,
        email: r.user.email,
        order: r.reviewOrder,
        isSigner: r.isSigner,
        isStaff: r.isStaff,
        reviewerStatus: r.reviewerStatus,
        signerNameOverride: r.signerNameOverride ?? undefined,
        signerEmailOverride: r.signerEmailOverride ?? undefined,
        streetAddress1: r.streetAddress1 ?? undefined,
        streetAddress2: r.streetAddress2 ?? undefined,
        city: r.city ?? undefined,
        state: r.state ?? undefined,
        zip: r.zip ?? undefined,
        country: r.country ?? undefined,
        dueDate: r.dueDate ? parseDateLikeToLocalDateTime(r.dueDate) : undefined,
      }));
      setReviewers(mappedReviewers.filter((r) => !r.isSigner));

      const updatedSigner = mappedReviewers.find((r) => r.isSigner);
      if (updatedSigner) {
        setSigner(updatedSigner);
      }
    }
  }, [hasInitializedForm, submissionData?.submission?.reviewers]);

  // Sync comments when submission data is refetched (e.g., after a status update)
  // This is separate from initialization to handle updates after the form is loaded
  useEffect(() => {
    if (hasInitializedForm && submissionData?.submission?.comments) {
      setComments(submissionData.submission.comments);
    }
  }, [hasInitializedForm, submissionData?.submission?.comments]);

  const updateSubmission = useCallback(
    async (input: UpdateSubmissionInput, intent: SubmitIntent) => {
      const result = await updateSubmissionMutation({
        variables: { input },
      });

      const updatedSubmission = result.data?.updateSubmission;

      if (!updatedSubmission) {
        return undefined;
      }

      if (
        intent === "SAVE_DRAFT" &&
        (updatedSubmission.status === "NOT_STARTED" || updatedSubmission.status === "IN_PROGRESS")
      ) {
        alerts.success(
          <SubmissionAlert
            submission={updatedSubmission}
            message="This draft has successfully been saved into Current Submissions."
            title="Draft saved"
            includeViewSubmissionLink={true}
          />
        );
      } else if (intent === "SAVE_DRAFT") {
        alerts.success("Submission updated successfully");
      }

      return updatedSubmission;
    },
    [alerts, updateSubmissionMutation]
  );

  const saveDocumentsOnly = useCallback(async () => {
    if (!submissionId) return;

    const input: UpdateSubmissionInput = {
      id: submissionId,
      submissionDocuments,
      attachmentDocuments,
    };

    const updated = await updateSubmission(input, SubmitIntent.SAVE_DRAFT);
    if (updated) {
      onSubmit?.({ submissionId: updated.id });
    }
  }, [submissionId, submissionDocuments, attachmentDocuments, updateSubmission, onSubmit]);

  const moveToReview = useCallback(
    async (input: UpdateSubmissionInput) => {
      const result = await moveToReviewMutation({
        variables: { input },
      });

      const updatedSubmission = result.data?.moveToReview;

      if (!updatedSubmission) {
        return undefined;
      }

      const advancedWithoutReview =
        updatedSubmission.status === SubmissionStatus.ProcessingSubmission;

      alerts.success(
        <SubmissionAlert
          submission={updatedSubmission}
          message={
            advancedWithoutReview
              ? "This submission has no reviewers and has been moved to Processing."
              : "This submission has been sent to reviewer(s) and moved to In-Progress."
          }
          title={
            advancedWithoutReview
              ? "Submission Moved to Processing"
              : "Submission Sent to Reviewers"
          }
          includeViewSubmissionLink={true}
        />
      );

      return updatedSubmission;
    },
    [alerts, moveToReviewMutation]
  );

  const handleFormSubmit = async (
    data: SubmissionFormData,
    intent: SubmitIntent = SubmitIntent.SAVE_DRAFT
  ) => {
    if (!tenantId) {
      alerts.error("Tenant ID is required");
      return;
    }

    if (isEditMode && !hasInitializedForm) {
      alerts.error("Please wait for the submission to finish loading");
      return;
    }

    try {
      const signerRequired = data.signatureType !== SignatureType.None;
      const signatureTypeEnum = data.signatureType;
      const allReviewers = compact([...reviewers, signer]);

      // Separate staff and non-staff reviewers to ensure proper ordering
      const staffReviewers = allReviewers.filter((r) => r.isStaff ?? false);
      const nonStaffReviewers = allReviewers.filter((r) => !(r.isStaff ?? false));

      // Map reviewers with proper reviewOrder:
      // - Staff reviewers get reviewOrder = 0
      // - Non-staff reviewers get sequential numbering (1, 2, 3...)
      const mappedReviewers: SubmissionReviewerInput[] = [
        ...staffReviewers.map((reviewer) => ({
          userId: reviewer.id,
          reviewOrder: 0,
          isStaff: true,
          isRequired: true,
          isSigner: reviewer.isSigner ?? false,
          signerNameOverride: reviewer.signerNameOverride || null,
          signerEmailOverride: reviewer.signerEmailOverride || null,
          streetAddress1: reviewer.streetAddress1 || undefined,
          streetAddress2: reviewer.streetAddress2 || undefined,
          city: reviewer.city || undefined,
          state: reviewer.state || undefined,
          zip: reviewer.zip || undefined,
          country: reviewer.country || undefined,
          dueDate: reviewer.dueDate
            ? (toUtcMidnightIsoFromDateLike(reviewer.dueDate) ?? undefined)
            : undefined,
          reviewerStatus: reviewer.reviewerStatus ?? undefined,
        })),
        ...nonStaffReviewers.map((reviewer, index) => ({
          userId: reviewer.id,
          reviewOrder: index + 1,
          isStaff: false,
          isRequired: true,
          isSigner: reviewer.isSigner ?? false,
          signerNameOverride: reviewer.signerNameOverride || null,
          signerEmailOverride: reviewer.signerEmailOverride || null,
          streetAddress1: reviewer.streetAddress1 || undefined,
          streetAddress2: reviewer.streetAddress2 || undefined,
          city: reviewer.city || undefined,
          state: reviewer.state || undefined,
          zip: reviewer.zip || undefined,
          country: reviewer.country || undefined,
          dueDate: reviewer.dueDate
            ? (toUtcMidnightIsoFromDateLike(reviewer.dueDate) ?? undefined)
            : undefined,
          reviewerStatus: reviewer.reviewerStatus ?? undefined,
        })),
      ];

      if (submissionId) {
        const input: UpdateSubmissionInput = {
          id: submissionId,
          title: data.title,
          submissionType: data.submissionType,
          recipient: data.recipient,
          submissionMethod: data.submissionMethod,
          notes: data.notes || "",
          dueDate: data.dueDate
            ? (toUtcMidnightIsoFromDateLike(data.dueDate) ?? undefined)
            : undefined,
          reviewRequired: data.reviewRequired,
          peerReviewRequired: data.peerReviewRequired,
          reviewMode: data.reviewMode,
          signatureType: signatureTypeEnum,
          staffAssigneeId: staffAssignee?.id ?? null,
          reviewers: mappedReviewers,
          facilityIds: data.facilityIds,
          submissionDocuments: submissionDocuments,
          attachmentDocuments: attachmentDocuments,
        };

        if (intent === SubmitIntent.MOVE_TO_REVIEW) {
          const updatedSubmission = await moveToReview(input);
          if (updatedSubmission) {
            onSubmit?.({ submissionId: updatedSubmission.id });
          }
        } else {
          await updateSubmission(input, intent);

          if (intent === SubmitIntent.PREPARE_FOR_SIGNING) {
            onSubmit?.({ submissionId, openPreparationDialog: true });
          } else {
            onSubmit?.({ submissionId });
          }
        }
      } else {
        const input: CreateSubmissionInput = {
          virtualEventId: virtualTaskId || "",
          tenantId,
          title: data.title,
          submissionType: data.submissionType as SubmissionType,
          recipient: data.recipient,
          submissionMethod: data.submissionMethod as SubmissionMethod,
          notes: data.notes || "",
          dueDate: data.dueDate
            ? (toUtcMidnightIsoFromDateLike(data.dueDate) ?? undefined)
            : undefined,
          reviewRequired: data.reviewRequired,
          peerReviewRequired: data.peerReviewRequired,
          reviewMode: data.reviewMode,
          signatureType: signatureTypeEnum,
          staffAssigneeId: staffAssignee?.id ?? null,
          reviewers: mappedReviewers,
          facilityIds: data.facilityIds,
          submissionDocuments: submissionDocuments,
          attachmentDocuments: attachmentDocuments,
          comments: comments.map((c) => pick(c, ["comment"])),
          moveToReview: intent === SubmitIntent.MOVE_TO_REVIEW,
        };

        const result = await createSubmission({
          variables: { input },
        });

        if (result.data?.createSubmission) {
          const createdSubmission = result.data.createSubmission;
          if (
            createdSubmission.status === SubmissionStatus.NotStarted ||
            createdSubmission.status === SubmissionStatus.InProgress
          ) {
            alerts.success(
              <SubmissionAlert
                submission={createdSubmission}
                message="This draft has successfully been saved into Current Submissions."
                title="Draft saved"
                includeViewSubmissionLink={true}
              />
            );
          } else {
            alerts.success("Submission created successfully");
          }

          if (intent === SubmitIntent.PREPARE_FOR_SIGNING) {
            onSubmit?.({
              submissionId: createdSubmission.id,
              openPreparationDialog: true,
            });
          } else {
            onSubmit?.({ submissionId: createdSubmission.id });
          }
        }
      }
    } catch (error) {
      alerts.error(
        isEditMode ? "Failed to update submission" : "Failed to create submission",
        error
      );
    }
  };

  const reviewStates = useMemo(
    () => computeSubmissionReviewStates({ currentUser: user, submissionData }),
    [user, submissionData]
  );

  return {
    form,
    reviewers,
    setReviewers,
    signer,
    setSigner,
    staffAssignee,
    setStaffAssignee,
    handleFormSubmit,
    saveDocumentsOnly,
    needsDocumentPreparation,
    loading: creating || updating || loadingSubmission || moveToReviewLoading,
    submissionDocuments: submissionData?.submission?.documents || [],
    submissionPayments: submissionData?.submission?.payments || [],
    submissionData,
    refetchSubmission,
    comments,
    setComments,
    hasInitializedForm,
    isFormReady: !isEditMode || (!!submissionData?.submission && hasInitializedForm),
    // Hide all issues for completed submissions - we don't care about compliance errors
    issues:
      submissionData?.submission?.status === SubmissionStatus.FilingComplete ||
      submissionData?.submission?.status === SubmissionStatus.NotReporting
        ? []
        : issues,
    errors,
    isValidating,

    dueDateWarningsByUserId,

    // Review state values
    reviewState: reviewStates.reviewState,
    currentUsersSubmissionDocumentReviewId: reviewStates.currentUsersSubmissionDocumentReviewId,
    isReviewerASigner: reviewStates.isReviewerASigner,
    nextReviewer: reviewStates.nextReviewer,
  };
}
